import type { Editor } from '@tiptap/core';
import {
  AlignCenter, AlignJustify, AlignLeft, AlignRight, Asterisk, Bold, ClipboardPaste, Download, Ellipsis, FileType, History, Maximize2, Minimize2,
  Save, Italic, Redo2, Search, Sparkles, SpellCheck, Table, Type, Undo2, X,
} from 'lucide-react';
import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Spinner } from '@/components/ui/spinner';
import { CORES_REALCE, CORES_TEXTO, ENTRELINHAS, ESTILOS, FONTES, ORDEM_ESTILOS, PREFIXO_PERSONALIZADO, TAMANHOS, formatoDoPronto, nomeDoEstilo, resumoDoFormato, type Alinhamento } from '../estilos';
import { FORMATOS_DO_ARQUIVO, type AcoesEditor, type EstadoEditor } from './barras';
import { Botao, BotaoNu } from './comum';

export type Gaveta = 'estilos' | 'formatar' | 'mais' | null;

/**
 * Uma ação do app no menu "Mais" do celular (ex.: baixar a minuta) — no lugar
 * dos `extrasBarra`, que no telefone tomavam uma linha inteira da barra.
 */
export interface AcaoMais {
  id: string;
  rotulo: string;
  icone: ReactNode;
  onSelect: () => void;
  /** Gerando agora: spinner no item, e o menu continua aberto até terminar. */
  ocupado?: boolean;
  /** Por que não dá agora — desabilita o item e aparece embaixo do rótulo. */
  motivoIndisponivel?: string | null;
}

export interface TelaCheia {
  ativa: boolean;
  alternar: () => void;
}

const ico = (I: typeof Bold) => <I className="cm-ef-ico" aria-hidden />;

/**
 * NO CELULAR, SÓ O ESSENCIAL — revisar e corrigir.
 *
 * Uma linha de botões de 44px que cabe em 320px — o estilo fica com a sobra da
 * linha, e o sublinhado mora na gaveta "Formatar" (docs/PLANO_RESPONSIVIDADE_MOBILE.md
 * §3.6); o resto em duas gavetas que sobem do pé da tela. Régua, painel Parágrafo, margens, colunas, timbrado e fórmulas ficam
 * no computador: no telefone a peça aparece com tudo isso já aplicado.
 */
export function BarraCelular({ ed, est, aoAbrir }: { ed: Editor; est: EstadoEditor; ac: AcoesEditor; aoAbrir: (g: Gaveta) => void }) {
  return (
    <div className="cm-ef-barra cm-ef-barra-cel" role="toolbar" aria-label="Formatação">
      <Botao semDica dica={{ nome: 'Desfazer' }} icone={ico(Undo2)} onClick={() => ed.chain().focus().undo().run()} />
      <BotaoNu className="cm-ef-bt cm-ef-sel cm-ef-sel-estilo-cel" onMouseDown={(e) => e.preventDefault()} onClick={() => aoAbrir('estilos')} aria-label="Estilo do parágrafo">
        <span className="cm-ef-bt-rot">{nomeDoEstilo(est.estilo)}</span>
      </BotaoNu>
      <Botao semDica dica={{ nome: 'Negrito' }} icone={ico(Bold)} ativo={est.negrito} onClick={() => ed.chain().focus().toggleBold().run()} />
      <Botao semDica dica={{ nome: 'Itálico' }} icone={ico(Italic)} ativo={est.italico} onClick={() => ed.chain().focus().toggleItalic().run()} />
      <Botao semDica dica={{ nome: 'Formatar' }} icone={ico(Type)} onClick={() => aoAbrir('formatar')} />
      <Botao semDica dica={{ nome: 'Mais' }} icone={ico(Ellipsis)} className="cm-ef-bt-fixo" onClick={() => aoAbrir('mais')} />
    </div>
  );
}

function GavetaBase({ titulo, aoFechar, children }: { titulo: string; aoFechar: () => void; children: ReactNode }) {
  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') aoFechar(); };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [aoFechar]);
  return createPortal(
    <div className="cm-ef-gaveta-veu cm-ef-portal" onClick={aoFechar}>
      <div className="cm-ef-gaveta" role="dialog" aria-label={titulo} onClick={(e) => e.stopPropagation()} onMouseDown={(e) => { if (!(e.target as HTMLElement).closest('input')) e.preventDefault(); }}>
        <div className="cm-ef-alca" aria-hidden />
        <div className="cm-ef-gaveta-cab">
          <h2>{titulo}</h2>
          <BotaoNu className="cm-ef-bt" aria-label="Fechar" onClick={aoFechar}>{ico(X)}</BotaoNu>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}

const Chip = ({ ativo, onClick, children, rotulo }: { ativo?: boolean; onClick: () => void; children: ReactNode; rotulo?: string }) => (
  <BotaoNu className={`cm-ef-chip${ativo ? ' is-ativo' : ''}`} aria-label={rotulo} aria-pressed={ativo} onClick={onClick}>{children}</BotaoNu>
);

export function Gavetas({ gaveta, ed, est, ac, aoFechar, extras, telaCheia }: {
  gaveta: Gaveta; ed: Editor; est: EstadoEditor; ac: AcoesEditor; aoFechar: () => void; extras?: AcaoMais[]; telaCheia?: TelaCheia;
}) {
  if (!gaveta) return null;
  const f = est.formato;

  if (gaveta === 'estilos') {
    return (
      <GavetaBase titulo="Estilo do parágrafo" aoFechar={aoFechar}>
        <div className="cm-ef-lista-op">
          {ORDEM_ESTILOS.map((k) => (
            <BotaoNu key={k} className={`cm-ef-op${k === est.estilo ? ' is-ativo' : ''}`} onClick={() => { ed.chain().focus().definirEstilo(k).run(); aoFechar(); }}>
              <span className="cm-ef-op-txt">{ESTILOS[k].nome}<span className="cm-ef-op-det">{resumoDoFormato(formatoDoPronto(k))}</span></span>
            </BotaoNu>
          ))}
          {ac.estilos.personalizados.map((p) => {
            const id = `${PREFIXO_PERSONALIZADO}${p.id}`;
            return (
              <BotaoNu key={p.id} className={`cm-ef-op${id === est.estilo ? ' is-ativo' : ''}`} onClick={() => { ed.chain().focus().definirEstilo(id).run(); aoFechar(); }}>
                <span className="cm-ef-op-txt">{p.nome}<span className="cm-ef-op-det">{resumoDoFormato({ ...formatoDoPronto(p.baseadoEm), ...p.ajustes })}</span></span>
              </BotaoNu>
            );
          })}
        </div>
      </GavetaBase>
    );
  }

  if (gaveta === 'formatar') {
    const alinh: [Alinhamento, string, typeof Bold][] = [['left', 'Esquerda', AlignLeft], ['center', 'Centro', AlignCenter], ['right', 'Direita', AlignRight], ['justify', 'Justificado', AlignJustify]];
    const i = TAMANHOS.indexOf(f.tamanho);
    return (
      <GavetaBase titulo="Formatar" aoFechar={aoFechar}>
        <h3>Texto</h3>
        <div className="cm-ef-chips">
          <Chip ativo={est.sublinhado} onClick={() => ed.chain().focus().toggleUnderline().run()}><u>Sublinhado</u></Chip>
          <Chip ativo={est.tachado} onClick={() => ed.chain().focus().toggleStrike().run()}><s>Tachado</s></Chip>
          <Chip ativo={est.sobrescrito} onClick={() => ed.chain().focus().toggleSuperscript().run()}>x² Sobrescrito</Chip>
          <Chip onClick={() => ac.aplicarTamanho(TAMANHOS[Math.max(0, (i < 0 ? 3 : i) - 1)])}>A− Menor</Chip>
          <Chip onClick={() => ac.aplicarTamanho(TAMANHOS[Math.min(TAMANHOS.length - 1, (i < 0 ? 3 : i) + 1)])}>A+ Maior</Chip>
        </div>
        <h3>Fonte</h3>
        <div className="cm-ef-chips">
          {FONTES.map((x) => <Chip key={x.nome} ativo={x.nome === f.fonte} onClick={() => ac.aplicarFonte(x.nome)}><span style={{ fontFamily: x.pilha }}>{x.nome}</span></Chip>)}
        </div>
        <h3>Cor e realce</h3>
        <div className="cm-ef-cores">
          {CORES_TEXTO.map((c) => <BotaoNu key={c} className="cm-ef-cor" style={{ background: c }} aria-label={`Cor ${c}`} onClick={() => ed.chain().focus().setColor(c).run()} />)}
          {CORES_REALCE.map((c) => <BotaoNu key={c} className="cm-ef-cor" style={{ background: c }} aria-label={`Realce ${c}`} onClick={() => ed.chain().focus().setHighlight({ color: c }).run()} />)}
          <BotaoNu className="cm-ef-cor cm-ef-cor-nenhuma" aria-label="Sem realce" onClick={() => ed.chain().focus().unsetHighlight().run()} />
        </div>
        <h3>Alinhamento</h3>
        <div className="cm-ef-chips">
          {alinh.map(([a, nome, I]) => <Chip key={a} rotulo={nome} ativo={f.alinhamento === a} onClick={() => ac.ajustar({ alinhamento: a })}>{ico(I)}</Chip>)}
        </div>
        <h3>Entrelinha</h3>
        <div className="cm-ef-chips">
          {ENTRELINHAS.filter((e) => e.valor !== 1.15).map((e) => <Chip key={e.valor} ativo={e.valor === f.entrelinha} onClick={() => ac.ajustar({ entrelinha: e.valor })}>{e.rotulo}</Chip>)}
        </div>
        <h3>Recuo e listas</h3>
        <div className="cm-ef-chips">
          <Chip onClick={() => ac.ajustar({ recuoEsquerdo: Math.max(0, f.recuoEsquerdo - 1.25) })}>Diminuir recuo</Chip>
          <Chip onClick={() => ac.ajustar({ recuoEsquerdo: Math.min(10, f.recuoEsquerdo + 1.25) })}>Aumentar recuo</Chip>
          <Chip ativo={est.numerado} onClick={() => ed.chain().focus().numerar('alfa').run()}>a), b), c)</Chip>
          <Chip onClick={() => ed.chain().focus().numerar('romano').run()}>I, II, III</Chip>
        </div>
      </GavetaBase>
    );
  }

  const item = (I: typeof Bold, rotulo: string, fn: () => void) => (
    <BotaoNu className="cm-ef-op" onClick={() => { aoFechar(); fn(); }}>{ico(I)}<span className="cm-ef-op-txt">{rotulo}</span></BotaoNu>
  );
  return (
    <GavetaBase titulo="Mais" aoFechar={aoFechar}>
      <div className="cm-ef-lista-op">
        {ac.salvamento && item(Save, 'Salvar', ac.salvamento.salvar)}
        {extras?.map((a) => (
          <BotaoNu key={a.id} className="cm-ef-op" disabled={!!a.motivoIndisponivel || extras.some((x) => x.ocupado)} aria-busy={a.ocupado || undefined} onClick={a.onSelect}>
            {a.ocupado ? <Spinner size={16} className="text-inherit" label={`${a.rotulo}…`} /> : a.icone}
            <span className="cm-ef-op-txt">{a.rotulo}{a.motivoIndisponivel && <span className="cm-ef-op-det">{a.motivoIndisponivel}</span>}</span>
          </BotaoNu>
        ))}
        {ac.arquivo.formatos.map((f) => (
          <BotaoNu key={f} className="cm-ef-op" disabled={ac.arquivo.gerando !== null} aria-busy={ac.arquivo.gerando === f || undefined} onClick={() => ac.arquivo.baixar(f)}>
            {ac.arquivo.gerando === f ? <Spinner size={16} className="text-inherit" label="Gerando o arquivo…" /> : <Download className="cm-ef-ico" aria-hidden />}
            <span className="cm-ef-op-txt">Baixar em {FORMATOS_DO_ARQUIVO[f].nome}</span>
          </BotaoNu>
        ))}
        {telaCheia && item(telaCheia.ativa ? Minimize2 : Maximize2, telaCheia.ativa ? 'Sair da tela cheia' : 'Tela cheia', telaCheia.alternar)}
        {item(Redo2, 'Refazer', () => ed.chain().focus().redo().run())}
        {item(ClipboardPaste, 'Colar', () => ac.colar('formatado'))}
        {item(Table, 'Inserir tabela', () => ed.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run())}
        {item(Asterisk, 'Nota de rodapé', () => ed.chain().focus().inserirNota().run())}
        {item(SpellCheck, 'Ortografia', ac.verificarOrtografia)}
        {ac.temIA && item(Sparkles, 'Revisar com IA', ac.revisarComIA)}
        {item(Search, 'Localizar e substituir', () => ac.abrirLateral('busca'))}
        {item(History, 'Versões', () => ac.abrirLateral('versoes'))}
        {ac.abrirFormatacao && item(FileType, 'Formatação do texto', ac.abrirFormatacao)}
      </div>
      <p className="cm-ef-nota-cel">Ficam no computador: régua, painel Parágrafo com medidas, margens, colunas, papel timbrado, edição de fórmulas e numeração de folhas. No celular a peça aparece com tudo isso já aplicado.</p>
    </GavetaBase>
  );
}
